import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  GET_VIP_PURCHASE_SETTINGS,
  SET_VIP_PURCHASE_ITEM,
} from "../../../queries/mutation/broadcaster/vipQueries";

type MenuOption = {
  type: string;
  name: string;
  cost: number;
  isActive: boolean;
};

type Props = {
  onClose: () => void;
  onSaved: () => void;
};

// Which menu item buys a month of VIP.
const VipPurchaseModal = ({ onClose, onSaved }: Props) => {
  const { data, loading, error } = useQuery(GET_VIP_PURCHASE_SETTINGS, {
    fetchPolicy: "network-only",
  });
  const [save, { loading: saving }] = useMutation(SET_VIP_PURCHASE_ITEM);

  const settings = data?.getVipPurchaseSettings;
  const options: MenuOption[] = settings?.options ?? [];
  const savedType: string = settings?.item?.type ?? "";
  const savedNotify: boolean = settings?.notifyRenewals ?? true;
  const [itemType, setItemType] = useState("");
  const [notifyRenewals, setNotifyRenewals] = useState(true);

  useEffect(() => {
    setItemType(savedType);
  }, [savedType]);
  useEffect(() => {
    setNotifyRenewals(savedNotify);
  }, [savedNotify]);

  const picked = options.find((o) => o.type === itemType);
  // The saved item may have been taken off the menu since.
  const savedMissing = !!settings?.item && !settings.item.name;

  const handleSave = async () => {
    try {
      await save({ variables: { itemType: itemType || null, notifyRenewals } });
      toast.success(
        itemType === savedType
          ? "Saved"
          : picked
            ? `Buying ${picked.name} now adds a month of VIP`
            : "No menu item sells VIP now",
      );
      onSaved();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box">
        <h3 className="font-bold text-lg">Sell VIP on your menu</h3>
        <p className="text-sm text-base-content/60 mt-1">
          Each time someone redeems this item, they get a month of VIP - added
          on top of any VIP they have left, so a monthly subscription keeps it
          running. They need Discord linked to buy it, and refunding the
          redeem takes the month back.
        </p>

        {loading ? (
          <span className="loading loading-dots loading-sm mt-4" />
        ) : error ? (
          <p className="text-sm text-error mt-4">
            Couldn't load your menu: {error.message}
          </p>
        ) : (
          <>
            <label className="form-control mt-4">
              <span className="label-text mb-1">Menu item</span>
              <select
                className="select select-bordered"
                value={itemType}
                onChange={(e) => setItemType(e.target.value)}
                disabled={saving}
              >
                <option value="">None - no item sells VIP</option>
                {options.map((o) => (
                  <option key={o.type} value={o.type}>
                    {o.name} ({o.cost} CC){o.isActive ? "" : " - switched off"}
                  </option>
                ))}
              </select>
            </label>
            {savedMissing && !picked && itemType && (
              <p className="text-xs text-warning mt-2">
                The saved item isn't on your menu anymore - pick another one.
              </p>
            )}
            {picked && !picked.isActive && (
              <p className="text-xs text-warning mt-2">
                This item is switched off on your menu, so nobody can buy it
                until you turn it back on.
              </p>
            )}

            <label className="flex items-start justify-between gap-4 mt-4 pt-3 border-t border-base-300 cursor-pointer">
              <div>
                <div className="text-sm font-semibold">
                  DM me when someone's VIP auto-renews
                </div>
                <p className="text-xs text-base-content/60 mt-1">
                  You're always DM'd when someone buys VIP. Renewals can come
                  in bunches on the same day, so you can turn just those off.
                </p>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-primary shrink-0 mt-1"
                checked={notifyRenewals}
                onChange={(e) => setNotifyRenewals(e.target.checked)}
                disabled={saving}
                aria-label="DM me when someone's VIP auto-renews"
              />
            </label>
          </>
        )}

        <div className="modal-action">
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={
              saving ||
              loading ||
              !!error ||
              (itemType === savedType && notifyRenewals === savedNotify) ||
              (!!itemType && !picked)
            }
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VipPurchaseModal;
